import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { useLocation } from "react-router-dom";

const RightSidebarContext = createContext({});

export const RightSidebarProvider = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [rightSidebar, setRightSidebar] = useState(<h1>Right Sidebar</h1>);
  const [closable, setClosable] = useState(true); // To control the close button visibility, if using page default content, it's not closable.

  /**
   * store a default sidebar content and activationId, so that we can revert to it when the sidebar is closed.
   */
  const [defaultContent, setDefaultContent] = useState(null);
  /**
   * `activationId` is used to recognise equivalent right sidebar. Set it alongsite setRightSidebar()
   */
  const [activationId, setActivationId] = useState(null);
  const [size, setSize] = useState("md");
  const [topOffset, setTopOffset] = useState(0);

  // Memoized functions ??why??
  /**
   * Update the right sidebar content.
   * @param {React.ReactNode} content - The sidebar content
   * @param {boolean} isDefault - Whether to store as the page's default sidebar content, it will be restored when the sidebar is used and closed by other components.
   * @param {string} _activationId - Provide if isDefault=true. The activationId to use for the sidebar.
   */
  const updateRightSidebar = useCallback(
    (content, isDefault = false, _activationId = null) => {
      setRightSidebar(content);
      setClosable(!isDefault); // Default so it's not closable
      if (isDefault) {
        setDefaultContent({ content, activationId: _activationId });
      }
    },
    []
  );

  /**
   * Close the right sidebar.
   * @type {(function(boolean=): void)|*} - Forced close will not restore the default content and clear it out.
   */
  const doClose = useCallback(
    (forced = false) => {
      if (defaultContent && !forced) {
        console.log("Rightsidebar fallback to default content");
        setRightSidebar(defaultContent.content);
        setActivationId(defaultContent.activationId);
        setClosable(false);
      } else {
        // clean up
        setIsOpen(false);
        setRightSidebar(null);
        setDefaultContent(null);
        setActivationId(null);
        setClosable(true);
      }
    },
    [defaultContent]
  );

  // Close the right sidebar when the route changes
  const location = useLocation();
  const [myPathname, setMyPathname] = useState();

  const doOpen = useCallback(
    (id = null) => {
      console.log("RightSidebar opened with id", id);
      setActivationId(id);
      if (!isOpen) {
        setIsOpen(true);
      }
      setMyPathname(location.pathname);
    },
    [isOpen, location.pathname]
  );

  useEffect(() => {
    if (myPathname !== location.pathname) {
      console.log("Sidebar doClose() as route changed to:", location.pathname);
      doClose(true);
      setRightSidebar(null);
      setDefaultContent(null);
      setMyPathname(null);
    }
  }, [location.pathname, myPathname, doClose]);

  return (
    <RightSidebarContext.Provider
      value={{
        rightSidebar,
        setRightSidebar: updateRightSidebar, // Use memoized setter
        isOpen,
        doOpen, // Memoized
        doClose, // Memoized
        size,
        setSize,
        activationId,
        closable,
        topOffset,
        setTopOffset,
      }}
    >
      {children}
    </RightSidebarContext.Provider>
  );
};

/**
 * Custom hook to access the right sidebar context.
 */
export const useRightSidebar = () => useContext(RightSidebarContext);
